import { ExternalScenesMapBounds } from './external-scenes.models';

/** The part of a Leaflet bounds object the search needs. */
export interface MapBoundsLike {
  getNorth(): number;
  getSouth(): number;
  getEast(): number;
  getWest(): number;
}

/** The part of a Leaflet map the search needs. */
export interface MapInstanceLike {
  getBounds?: () => MapBoundsLike | null | undefined;
}

/**
 * Reads the visible map extent at the moment "Apply" is pressed.
 * Falls back to `lastKnown` (when it is usable itself) when the map is not ready, reports no bounds, or reports an unusable extent: Leaflet
 * returns a single point (north equals south, west equals east) while it still counts the container as empty, for
 * example right after a tab or dialog change, and the search must not be built from that.
 */
export function readMapBounds(
  mapInstance: MapInstanceLike | null | undefined,
  lastKnown: ExternalScenesMapBounds | null
): ExternalScenesMapBounds | null {
  const bounds = typeof mapInstance?.getBounds === 'function' ? mapInstance.getBounds() : null;
  if (!bounds) {
    return lastKnown;
  }
  const live: ExternalScenesMapBounds = {
    north: bounds.getNorth(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    west: bounds.getWest()
  };
  if (hasArea(live)) {
    return live;
  }
  return lastKnown && hasArea(lastKnown) ? lastKnown : null;
}

/** True when every edge is a finite number and the extent covers an area. */
function hasArea(bounds: ExternalScenesMapBounds): boolean {
  const edges = [bounds.north, bounds.south, bounds.east, bounds.west];
  return edges.every(Number.isFinite) && bounds.north > bounds.south && bounds.east > bounds.west;
}
